import {
  IRowSource,
  getLastRowGroupAsCoA,
  EAllLedgerTypeDisplay,
  ColumnValueSegment,
  ILensColumnReference,
  IDimensionColumn as IIDimensionColumn,
  IDimensionColumnReference,
  isDimensionColumn,
  UnifiedLedgerTypeDisplay
} from '@opengov/reporting-types';

import { IGroupBy, IRowEditorSetupFormValues, IRowOption, RowGroupOptions } from '../../../../types/MultiDatasetTypes';
import { IColumnOption, IColumnOptionSegment, IDimensionColumn } from '../../../../types/ColumnOptionTypes';

function createGroupBy(columnOption: IColumnOption, dimensionColumn: IDimensionColumn): IGroupBy {
  return { column: `${columnOption.name}:${dimensionColumn.name}`, dimensionId: dimensionColumn.dimensionId };
}

export const selectFirstOption = (rowOption: IRowOption): IGroupBy | null => {
  // This is assuming the first "column options segment" is "Segments", which
  // may not be true if data is not linked to CoA.
  const firstOption = rowOption.row[0].columnOptions[0];
  const firstChild = firstOption.dimensionColumns?.[0];
  if (firstChild?.name) {
    return createGroupBy(firstOption, firstChild);
  }
  return null;
};

export const findBestGroupByForShow = (rowOption: IRowOption): IGroupBy | null => {
  const showLabel = rowOption.label;

  if (showLabel === EAllLedgerTypeDisplay.al) {
    return selectFirstOption(rowOption);
  }

  const result = rowOption.row[0].columnOptions.find((opt) => {
    return (
      (opt.name as UnifiedLedgerTypeDisplay) === showLabel || (opt.label as UnifiedLedgerTypeDisplay) === showLabel
    );
  });
  return result && result.dimensionColumns ? createGroupBy(result, result.dimensionColumns[0]) : null;
};

export const validateForm = (values: IRowEditorSetupFormValues): boolean => {
  return values.show && values.groupBy !== null && values.groupName?.trim().length > 0;
};

const selectGroupByValue = (
  grouping: IIDimensionColumn | IDimensionColumnReference | ILensColumnReference,
  groupByOptions: IColumnOptionSegment[]
): IGroupBy => {
  const value = grouping.columnName;
  const options = groupByOptions.flatMap((optionSegment) => optionSegment.columnOptions);

  const segmentParent = options.find((opt) => {
    return opt.dimensionColumns?.find((child) => child.name === value);
  });

  const dimensionId = isDimensionColumn(grouping)
    ? grouping.dimension.id
    : segmentParent?.dimensionColumns?.find((child) => child.name === value)?.dimensionId;

  return {
    column: [segmentParent?.name, value].filter(Boolean).join(':'),
    dimensionId
  };
};

export const getFirstAvailableFormValues = (options: RowGroupOptions): IRowEditorSetupFormValues => {
  const firstOption = Object.values(options)[0];

  return {
    show: firstOption.value,
    groupBy: [findBestGroupByForShow(firstOption) || { column: '' }],
    groupName: firstOption.label
  };
};

export const getInitialFormValues = (
  rowGroups: IRowSource[],
  rowGroupIndex: number | null,
  options: RowGroupOptions
): IRowEditorSetupFormValues => {
  const rowGroup = getLastRowGroupAsCoA(rowGroups, rowGroupIndex);
  const show = rowGroup.showOnly.value;

  if (!(show in options)) return getFirstAvailableFormValues(options);

  return {
    show,
    groupBy: rowGroup.grouping.length
      ? rowGroup.grouping.map((grouping) => selectGroupByValue(grouping, options[rowGroup.showOnly.value].row))
      : [],
    groupName: rowGroup.groupName ?? options[show].label
  };
};

export const swapArrayElementsWithMutation = (array: unknown[], firstIndex: number, secondIndex: number): void => {
  if (firstIndex < 0 || secondIndex < 0) throw new Error('Index must be a positive integer.');
  if (firstIndex >= array.length || secondIndex >= array.length)
    throw new Error('Index must be within bounds of array.');
  const tmp = array[firstIndex];
  array[firstIndex] = array[secondIndex];
  array[secondIndex] = tmp;
};

export const isDuplicateColumnExists = (pivotColumns: ColumnValueSegment[][]): boolean => {
  const pivotColumnsMap = new Map<string, Set<string>>();

  for (const [col1, col2] of pivotColumns) {
    const col1Str = col1 as string;
    const col2Str = col2 as string;

    if (pivotColumnsMap.has(col1Str) && pivotColumnsMap.get(col1Str)?.has(col2Str)) {
      return true;
    }

    pivotColumnsMap.set(col1Str, pivotColumnsMap.get(col1Str) ?? new Set());
    pivotColumnsMap.get(col1Str)?.add(col2Str);
  }

  return false;
};
